import {
    buildKybReportSectionChildren,
    EXTERNAL_PROVIDER_REPORTS_SECTION_ID,
} from "src/modules/3rd-party-management/helpers/kybReports";

import {
    buildSectionMeta,
    buildTocEntries,
    resolveSectionDefinitions,
} from "../helpers/sectionRegistry";

import ManagementSummarySection from "./management-summary";
import CompanyOverviewSection from "./company-overview";
import BasicCompanyInformationSection from "./basic-company-information";
import ExternalProviderReportsSection from "./external-provider-reports";
import GroupStructureGraphSection from "./group-structure-graph";
import ComplianceMonitoringSection from "./compliance-monitoring";
import ComplianceScreeningSection from "./compliance-screening";
import ProductsSection from "./products";
import FindingsSection from "./findings";
import RelatedIncidentsSection from "./related-incidents";
import ReportsSection from "./reports";
import RiskAssessmentSection from "./risk-assessment";
import TaskManagementSection from "./task-management";

/**
 * Ordered section definitions. Add/reorder sections here only.
 * `placement` controls which document page group renders the section.
 */
export const SECTION_DEFINITIONS = [
    {
        id: "management-summary",
        title: "Management Summary",
        numbered: false,
        placement: "management-summary",
        Component: ManagementSummarySection,
    },
    {
        id: "company-overview",
        title: "Company Overview",
        numbered: false,
        placement: "overview",
        Component: CompanyOverviewSection,
    },
    {
        id: "basic-company-information",
        title: "Basic Company Information",
        numbered: true,
        placement: "overview",
        Component: BasicCompanyInformationSection,
        children: [
            { id: "address", title: "Address" },
            { id: "contact-information", title: "Contact Information" },
            { id: "shareholders", title: "Shareholders" },
            { id: "management-team", title: "Management Team" },
            { id: "financial-summary", title: "Financial Summary" },
            { id: "metadata", title: "Metadata" },
        ],
    },
    {
        id: EXTERNAL_PROVIDER_REPORTS_SECTION_ID,
        title: "External Provider Reports",
        numbered: true,
        placement: "body",
        Component: ExternalProviderReportsSection,
        resolveChildren: ({ kybReports } = {}) => buildKybReportSectionChildren(kybReports),
    },
    {
        id: "group-structure-graph",
        title: "Group Structure Graph",
        numbered: true,
        placement: "body",
        Component: GroupStructureGraphSection,
    },
    {
        id: "compliance-monitoring",
        title: "Compliance Monitoring",
        numbered: true,
        placement: "body",
        Component: ComplianceMonitoringSection,
    },
    {
        id: "compliance-screening",
        title: "Compliance Screening",
        numbered: true,
        placement: "body",
        Component: ComplianceScreeningSection,
    },
    {
        id: "products",
        title: "Products",
        numbered: true,
        placement: "body",
        Component: ProductsSection,
        children: [
            { id: "products-detail", title: "Products Detail" },
            { id: "deforestation-due-diligence", title: "Deforestation Due Diligence" },
        ],
    },
    {
        id: "findings",
        title: "Findings",
        numbered: true,
        placement: "body",
        Component: FindingsSection,
    },
    {
        id: "related-incidents",
        title: "Related Incidents",
        numbered: true,
        placement: "body",
        Component: RelatedIncidentsSection,
    },
    {
        id: "reports",
        title: "Reports",
        numbered: true,
        placement: "body",
        Component: ReportsSection,
    },
    {
        id: "risk-assessment",
        title: "Risk Assessment",
        numbered: true,
        placement: "body",
        Component: RiskAssessmentSection,
    },
    {
        id: "task-management",
        title: "Task Management",
        numbered: true,
        placement: "body",
        Component: TaskManagementSection,
    },
];

export const SECTION_META = buildSectionMeta(SECTION_DEFINITIONS);
export const TOC_ENTRIES = buildTocEntries(SECTION_META);

export const buildExportSectionMeta = (context = {}) =>
    buildSectionMeta(resolveSectionDefinitions(SECTION_DEFINITIONS, context));

export const buildExportTocEntries = (context = {}) =>
    buildTocEntries(buildExportSectionMeta(context));

export const getSectionsByPlacement = (placement, sectionMeta = SECTION_META) =>
    sectionMeta.filter((section) => section.placement === placement);
